import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Send, ArrowLeft, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AppHeader } from "@/components/AppHeader";
import { getConversations, getListings, sendMessage, CURRENT_USER, type Conversation } from "@/data/listings";
import { formatDistanceToNow } from "date-fns";

const QUICK_REPLIES = [
  "Is this still available?",
  "Can you do a lower price?",
  "I'm interested! Can we meet today?",
  "Can I see more photos?",
];

export default function Messages() {
  const [searchParams] = useSearchParams();
  const convId = searchParams.get("conv");
  const [activeConv, setActiveConv] = useState<Conversation | null>(null);
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [localMessages, setLocalMessages] = useState<Conversation["messages"]>([]);
  const bottomRef = useRef<HTMLDivElement>(null);

  const { data: convos = [], refetch } = useQuery({
    queryKey: ["conversations", CURRENT_USER.id],
    queryFn: () => getConversations(CURRENT_USER.id),
  });

  const { data: listings = [] } = useQuery({
    queryKey: ["listings"],
    queryFn: getListings,
  });

  useEffect(() => {
    if (convId && convos.length) {
      const found = convos.find(c => c.id === convId);
      if (found) {
        setActiveConv(found);
        setLocalMessages(found.messages);
      }
    }
  }, [convId, convos]);

  const getListingForConv = (conv: Conversation) => listings.find(l => l.id === conv.listingId);
  const getOtherParty = (conv: Conversation) => {
    const listing = getListingForConv(conv);
    if (!listing) return { name: "Unknown", avatar: "" };
    if (conv.buyerId === CURRENT_USER.id) return { name: listing.sellerName, avatar: listing.sellerAvatar };
    return { name: CURRENT_USER.name, avatar: CURRENT_USER.avatar };
  };

  const handleSend = async (text?: string) => {
    const content = text || msg.trim();
    if (!content || !activeConv) return;
    setSending(true);
    setMsg("");
    const newMsg = await sendMessage(activeConv.id, CURRENT_USER.id, content);
    setLocalMessages(prev => [...prev, newMsg]);
    setSending(false);
    await refetch();
    setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
  };

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [localMessages]);

  return (
    <div className="min-h-screen pb-20 md:pb-0">
      <AppHeader />

      <div className="max-w-5xl mx-auto h-[calc(100vh-64px)] flex">
        {/* Sidebar - conversation list */}
        <div className={`w-full md:w-72 flex-shrink-0 border-r border-border/60 flex flex-col ${activeConv ? "hidden md:flex" : "flex"}`}>
          <div className="p-4 border-b border-border/60">
            <h2 className="font-semibold text-base">Messages</h2>
            <p className="text-xs text-muted-foreground mt-0.5">{convos.length} conversation{convos.length !== 1 ? "s" : ""}</p>
          </div>

          <div className="flex-1 overflow-y-auto">
            {convos.length === 0 ? (
              <div className="text-center py-12 px-4">
                <div className="text-3xl mb-2">💬</div>
                <p className="text-sm font-medium mb-1">No messages yet</p>
                <p className="text-xs text-muted-foreground">Start chatting with sellers from listings</p>
              </div>
            ) : (
              convos.map(conv => {
                const listing = getListingForConv(conv);
                const other = getOtherParty(conv);
                const isActive = activeConv?.id === conv.id;
                return (
                  <button
                    key={conv.id}
                    onClick={() => { setActiveConv(conv); setLocalMessages(conv.messages); }}
                    className={`w-full p-4 flex items-start gap-3 hover:bg-muted/40 transition-colors border-b border-border/40 text-left ${isActive ? "bg-accent" : ""}`}
                  >
                    <div className="relative flex-shrink-0">
                      <img src={listing?.images[0] || ""} alt="" className="w-10 h-10 rounded-xl object-cover" />
                      <img src={other.avatar} alt="" className="w-5 h-5 rounded-full absolute -bottom-1 -right-1 border border-card object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-1">
                        <p className="text-xs font-semibold truncate">{listing?.title || "Unknown listing"}</p>
                        <p className="text-xs text-muted-foreground flex-shrink-0">
                          {conv.lastTimestamp ? formatDistanceToNow(conv.lastTimestamp, { addSuffix: false }) : ""}
                        </p>
                      </div>
                      <p className="text-xs text-muted-foreground truncate mt-0.5">{conv.lastMessage}</p>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* Chat area */}
        {activeConv ? (
          <div className="flex-1 flex flex-col">
            {/* Chat header */}
            <div className="p-4 border-b border-border/60 flex items-center gap-3">
              <button
                onClick={() => setActiveConv(null)}
                className="md:hidden p-1 hover:bg-muted rounded-lg transition-colors"
              >
                <ArrowLeft size={16} />
              </button>
              {(() => {
                const listing = getListingForConv(activeConv);
                return listing ? (
                  <>
                    <img src={listing.images[0]} alt="" className="w-10 h-10 rounded-xl object-cover" />
                    <div>
                      <p className="text-sm font-semibold line-clamp-1">{listing.title}</p>
                      <p className="text-xs font-bold" style={{ color: "var(--rose)" }}>{listing.sellingPrice} SAR</p>
                    </div>
                  </>
                ) : null;
              })()}
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {localMessages.map(message => {
                const isMe = message.senderId === CURRENT_USER.id;
                return (
                  <div key={message.id} className={`flex ${isMe ? "justify-end" : "justify-start"}`}>
                    <div
                      className={`max-w-[70%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                        isMe
                          ? "text-white rounded-br-sm"
                          : "bg-muted text-foreground rounded-bl-sm"
                      }`}
                      style={isMe ? { background: "var(--rose)" } : {}}
                    >
                      {message.text}
                    </div>
                  </div>
                );
              })}
              <div ref={bottomRef} />
            </div>

            {/* Quick replies */}
            <div className="px-4 pb-2 flex gap-2 overflow-x-auto no-scrollbar">
              {QUICK_REPLIES.map(qr => (
                <button
                  key={qr}
                  onClick={() => handleSend(qr)}
                  className="flex-shrink-0 text-xs px-3 py-1.5 rounded-full border border-border/60 text-muted-foreground hover:border-rose-300 hover:text-rose-600 transition-all"
                >
                  {qr}
                </button>
              ))}
            </div>

            {/* Input */}
            <div className="p-4 border-t border-border/60">
              <div className="flex gap-2">
                <Input
                  placeholder="Type a message…"
                  value={msg}
                  onChange={e => setMsg(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && !e.shiftKey && handleSend()}
                  className="rounded-full text-sm border-border/60"
                />
                <Button
                  size="icon"
                  className="rounded-full flex-shrink-0"
                  style={{ background: "var(--rose)", color: "white" }}
                  onClick={() => handleSend()}
                  disabled={!msg.trim() || sending}
                >
                  {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex-1 hidden md:flex items-center justify-center">
            <div className="text-center">
              <div className="text-5xl mb-3">💬</div>
              <h3 className="font-semibold text-lg mb-1">Your messages</h3>
              <p className="text-sm text-muted-foreground">Select a conversation to start chatting</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
